import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  AppBar,
  Toolbar,
  IconButton,
  InputBase,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
  Chip
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  PlayArrow as GetStartedIcon,
  PlayArrow,
  Create as CreateIcon,
  Share as PublishIcon,
  Analytics as AnalyticsIcon,
  Settings as AdvancedIcon,
  Help as TroubleshootIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import MetaTags from '../components/SEO/MetaTags';

// Import documentation content components
import WhatIsSurveySlack from '../components/documentation/WhatIsSurveySlack';
import QuickStartGuide from '../components/documentation/QuickStartGuide';
import SurveyBuilderBasics from '../components/documentation/SurveyBuilderBasics';
import QuestionTypes from '../components/documentation/QuestionTypes';
import ShareOptions from '../components/documentation/ShareOptions';
import ResponseAnalytics from '../components/documentation/ResponseAnalytics';
import CreateSurveyTutorial from '../components/documentation/CreateSurveyTutorial';
import SurveyLogicTutorial from '../components/documentation/SurveyLogicTutorial';

const DRAWER_WIDTH = 280;

const documentationStructure = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: <GetStartedIcon />,
    items: [
      { id: 'sign-up-dashboard', title: 'Sign Up & Dashboard', component: WhatIsSurveySlack },
   
    ]
  },
  {
    id: 'building-surveys',
    title: 'Building Surveys',
    icon: <CreateIcon />,
    items: [
      { id: 'creating-survey', title: 'Creating a Survey', component: CreateSurveyTutorial },
      { id: 'survey-builder-basics', title: 'Survey Builder Basics', component: SurveyBuilderBasics },
      { id: 'question-types', title: 'Question Types', component: QuestionTypes },
      { id: 'survey-logic', title: 'Survey Logic', component: null }
    ]
  },
  {
    id: 'publishing-sharing',
    title: 'Publishing & Sharing',
    icon: <PublishIcon />,
    items: [
      { id: 'publishing-your-survey', title: 'Publishing & Share Your Survey', component: ShareOptions },
      // { id: 'share-options', title: 'Share Options', component: ShareOptions },
      { id: 'distribution-methods', title: 'Distribution Methods', component: null }
    ]
  },
  {
    id: 'analyzing-results',
    title: 'Analyzing Results',
    icon: <AnalyticsIcon />,
    items: [
      { id: 'viewing-analytics', title: 'Viewing Analytics', component: ResponseAnalytics },
      { id: 'exporting-data', title: 'Exporting Data', component: null },
      { id: 'understanding-metrics', title: 'Understanding Metrics', component: null }
    ]
  },
  {
    id: 'settings-collaboration',
    title: 'Settings & Collaboration',
    icon: <AdvancedIcon />,
    items: [
      { id: 'team-collaboration', title: 'Team Collaboration', component: null },
      { id: 'api-integration', title: 'API Integration', component: null }
    ]
  }
];

const Documentation = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { section, page } = useParams();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  // Initialize expanded sections and breadcrumbs
  useEffect(() => {
    const initialExpanded = {};
    documentationStructure.forEach(section => {
      initialExpanded[section.id] = true;
    });
    setExpandedSections(initialExpanded);

    // Set breadcrumbs based on current route
    if (!section || !page) {
      setBreadcrumbs([
        { title: 'Getting Started', path: '/documentations/getting-started' },
        { title: 'Sign Up & Dashboard', path: '/documentations/getting-started/sign-up-dashboard' }
      ]);
    } else {
      const sectionData = documentationStructure.find(s => s.id === section);
      const pageData = sectionData?.items.find(p => p.id === page);
      
      if (pageData) {
        setBreadcrumbs([
          { title: sectionData.title, path: `/documentations/${section}` },
          { title: pageData.title, path: `/documentations/${section}/${page}` }
        ]);
      }
    }
  }, [section, page]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSectionToggle = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handlePageSelect = (sectionId, pageId, component) => {
    if (component) {
      navigate(`/documentations/${sectionId}/${pageId}`);
      if (isMobile) {
        setMobileOpen(false);
      }
    }
  };

  const renderCurrentContent = () => {
    if (!section || !page) {
      return <WhatIsSurveySlack />;
    }

    const key = `${section}-${page}`;
    switch (key) {
      case 'getting-started-sign-up-dashboard':
        return <WhatIsSurveySlack />;
      case 'getting-started-quick-start':
        return <QuickStartGuide />;
      case 'building-surveys-creating-survey':
        return <CreateSurveyTutorial />;
      case 'building-surveys-survey-builder-basics':
        return <SurveyBuilderBasics />;
      case 'building-surveys-question-types':
        return <QuestionTypes />;
      case 'building-surveys-survey-logic':
        return <SurveyLogicTutorial />;
      case 'publishing-sharing-publishing-your-survey':
      case 'publishing-sharing-share-options':
        return <ShareOptions />;
      case 'analyzing-results-viewing-analytics':
        return <ResponseAnalytics />;
      default:
        return (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" gutterBottom>
              Welcome to SurveySlack Documentation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Select a topic from the sidebar to get started
            </Typography>
          </Box>
        );
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Drawer Title */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600,textAlign: 'center' }}>
          Documentation
        </Typography>
      </Box>

      {/* Navigation (scrollable) */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List sx={{ p: 0 }}>
          {documentationStructure.map((sec) => (
            <Box key={sec.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleSectionToggle(sec.id)}
                  sx={{
                    py: 1.5,
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {sec.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={sec.title}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                  />
                  {expandedSections[sec.id] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={expandedSections[sec.id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {sec.items.map((item) => {
                    const isActive = section === sec.id && page === item.id;
                    return (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          sx={{
                            pl: 7,
                            py: 2,
                            backgroundColor: isActive ? 'primary.lighter' : 'transparent',
                            // borderLeft: isActive ? '3px solid' : '3px solid transparent',
                            borderColor: isActive ? 'primary.main' : 'transparent',
                            '&:hover': { backgroundColor: isActive ? 'primary.lighter' : 'action.hover' }
                          }}
                          onClick={() => handlePageSelect(sec.id, item.id, item.component)}
                          disabled={!item.component}
                        >
                          <ListItemText 
                            primary={item.title}
                            primaryTypographyProps={{
                              fontSize: '0.85rem',
                              color: isActive ? 'primary.main' : (item.component ? 'text.secondary' : 'text.disabled')
                            }}
                          />
                          {!item.component && (
                            <Chip 
                              label="Soon" 
                              size="small" 
                              sx={{ height: 20, fontSize: '0.7rem', backgroundColor: 'grey.200', color: 'text.secondary' }} 
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          ))}
        </List>
      </Box>
      
      {/* Fixed footer: stays at bottom of drawer */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
          zIndex: 1
        }}
      >
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/')}
          sx={{ color: 'primary.main', textDecoration: 'none' }}
        >
          Back to Home
        </Link>
      </Box>
    </Box>
  );

  return (
    <>
      <MetaTags
        title="Documentation - SurveySlack"
        description="Learn how to create, publish, and analyze surveys with SurveySlack. Complete guides and tutorials."
        canonical="/documentations"
        keywords={[
          'survey documentation',
          'survey tutorial',
          'how to create surveys',
          'survey guide',
          'SurveySlack help'
        ]}
      />
      
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {/* App Bar */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
            ml: { md: `${DRAWER_WIDTH}px` },
            backgroundColor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            color: 'text.primary'
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            {/* <Box
              sx={{
                p: '2px 20px',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                
                // width: { xs: '100%', sm: 520, md: 720 },
                // ml: 'auto',
                // backgroundColor: 'none'
              }}
            >
              <SearchIcon sx={{ p: '10px', color: 'text.secondary' }} />
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box> */}
          
<Box sx={{ flex: 1 }} />
            <IconButton
              color="inherit"
              onClick={() => navigate('/dashboard')}
              sx={{ ml: 0 }}
            >
              <CloseIcon />
            </IconButton>
            {/* </Box> */}
          </Toolbar>
        </AppBar>

        {/* Sidebar */}
        <Box
          component="nav"
          sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: DRAWER_WIDTH,
                borderRight: '1px solid',
                borderColor: 'divider'
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
            backgroundColor: 'background.default'
          }}
        >
          <Toolbar />
          <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
            {/* Breadcrumbs */}
            {breadcrumbs.length > 0 && (
              <Breadcrumbs sx={{ mb: 3 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/documentations')}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  Documentation
                </Link>
                {breadcrumbs.map((crumb, index) => (
                  <Typography key={index} color="text.primary" variant="body2">
                    {crumb.title}
                  </Typography>
                ))}
              </Breadcrumbs>
            )}

            {/* Content */}
            <Paper 
              sx={{ 
                p: { xs: 3, sm: 4 }, 
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
{renderCurrentContent()}
            </Paper>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Documentation;